import {
  buildProjectionSegments,
  MAX_GRID_SEGMENTS,
} from '@modules/editor/src/plugins/grid/utils/projection';
import { ensureLoadedImage } from '@modules/editor/src/utils/image';
import { type Point } from '@modules/editor/src/utils/math';
import {
  getPerspectiveTransform,
  INVERSE_BILINEAR_GLSL,
} from '@modules/editor/src/utils/perspective';
import * as twgl from 'twgl.js';

import {
  getCurrentView,
  getVisibleComponentIds,
} from '$/pages/InspirationEditorPage/lib/selectors';
import { type InspirationEditorState } from '$/pages/InspirationEditorPage/stores/useInspirationEditorStore';
import { type DetailedInspiration } from '$/services/usecases/inspirations/mapper/inspiration.schema';
import { hexToRgb } from '$/utils/colorUtils';

export type Mapping = {
  mask: string;
  material: string | undefined;
  is_texture_material: boolean;
  corners: Point[];
  cornerLines?: Point[][];
  object_width: number;
  object_height: number;
  rotation: number;
  real_texture_width: number;
  real_texture_height: number;
};

type MaterialEntry = {
  texture: WebGLTexture;
  width: number;
  height: number;
};

type State = {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  layerTextures: Map<string, WebGLTexture>;
  maskTextures: Map<string, WebGLTexture>;
  maskData: Map<string, Uint8ClampedArray>;
  materialCache: Map<string, MaterialEntry | Promise<MaterialEntry | null>>;
  diffuseFramebuffer: WebGLFramebuffer;
  diffuseTexture: WebGLTexture;
  blitProgram: twgl.ProgramInfo;
  materialProgram: twgl.ProgramInfo;
  compositeProgram: twgl.ProgramInfo;
  fullScreenBuffer: twgl.BufferInfo;
  readBuffer: Uint8Array;
};

let state: State | null = null;

const LAYERS = [
  'layer_diffuse',
  'layer_lighting',
  'layer_global_illumination',
  'layer_detail',
] as const;

const hexToSrgb = (hex: string): [number, number, number] => {
  const [r, g, b] = hexToRgb(hex);
  return [r / 255, g / 255, b / 255];
};

const FULLSCREEN_PASS_VS = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const BLIT_FS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
out vec4 outColor;
void main() {
  outColor = vec4(texture(u_texture, v_uv).rgb, 0.0);
}`;

const MATERIAL_FS = `#version 300 es
precision highp float;
#define MAX_SEGMENTS ${MAX_GRID_SEGMENTS}
in vec2 v_uv;
uniform vec2 u_imageSize;
uniform sampler2D u_mask;
uniform int u_isTexture;
uniform vec3 u_color;
uniform sampler2D u_material;
uniform int u_segmentCount;
uniform mat3 u_matrices[MAX_SEGMENTS];
uniform vec2 u_segmentCorners[MAX_SEGMENTS * 4]; // TL, TR, BR, BL per segment
uniform vec3 u_cornerLines[MAX_SEGMENTS - 1]; // line equation, > 0 right of the line
uniform vec2 u_sourceQuadSize;
uniform vec2 u_scaledTextureSize;
uniform float u_rotation;
out vec4 outColor;

vec3 srgbToLinear(vec3 c) {
  vec3 lo = c / 12.92;
  vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
  return mix(lo, hi, step(vec3(0.04045), c));
}
${INVERSE_BILINEAR_GLSL}
// Linear V so neighboring segments line up along their shared corner line.
float getLinearV(vec2 p, int segment, float estimate) {
  float v1;
  float v2;
  if (!bilinearVRoots(
    p,
    u_segmentCorners[segment * 4],
    u_segmentCorners[segment * 4 + 1],
    u_segmentCorners[segment * 4 + 2],
    u_segmentCorners[segment * 4 + 3],
    v1,
    v2
  )) {
    return estimate;
  }

  // pick the root closest to the estimate, works outside the segment too
  return abs(v1 - estimate) < abs(v2 - estimate) ? v1 : v2;
}

void main() {
  float m = texture(u_mask, v_uv).r;
  if (m < 0.5) discard;

  if (u_isTexture == 0) {
    outColor = vec4(srgbToLinear(u_color), m);
    return;
  }

  vec2 v_pixel = v_uv * u_imageSize;

  int segment = 0;
  for (int i = 0; i < u_segmentCount - 1; i++) {
    if (dot(u_cornerLines[i], vec3(v_pixel, 1.0)) > 0.0) segment++;
  }

  vec3 projected = u_matrices[segment] * vec3(v_pixel, 1.0);
  float srcX = projected.x / projected.z;
  float srcY = projected.y / projected.z;

  if (u_segmentCount > 1) {
    float halfH = u_sourceQuadSize.y * 0.5;
    float estimate = (srcY + halfH) / u_sourceQuadSize.y;
    srcY = mix(-halfH, halfH, getLinearV(v_pixel, segment, estimate));
  }

  float angle = -radians(u_rotation);
  float cx = u_sourceQuadSize.x * 0.5;
  float cy = u_sourceQuadSize.y * 0.5;
  float tx = srcX - cx;
  float ty = srcY - cy;
  float c = cos(angle);
  float s = sin(angle);
  float rx = tx * c - ty * s;
  float ry = tx * s + ty * c;
  float fx = rx + cx;
  float fy = ry + cy;

  vec2 uv = vec2(fx, fy) / u_scaledTextureSize;
  vec4 texColor = texture(u_material, uv);
  outColor = vec4(srgbToLinear(texColor.rgb), m);
}`;

const COMPOSITE_FS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_diffuse;
uniform sampler2D u_lighting;
uniform sampler2D u_gi;
uniform sampler2D u_detail;
uniform int u_has_gray;
uniform sampler2D u_gray;
out vec4 outColor;

vec3 linearToSrgb(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}

void main() {
  vec4 diffuseSample = texture(u_diffuse, v_uv);
  vec3 d = diffuseSample.rgb;
  float edited = diffuseSample.a;
  vec3 l = texture(u_lighting, v_uv).rgb;
  vec3 g = texture(u_gi, v_uv).rgb;
  vec3 dt = texture(u_detail, v_uv).rgb;

  vec3 result;
  if (u_has_gray == 1 && edited > 0.5) {
    vec3 linearGray = texture(u_gray, v_uv).rgb;

    float grayAlbedoLinear = 0.214; 

    vec3 pureLighting = linearGray / grayAlbedoLinear;
    result = d * pureLighting;
  } else {
    result = d * (l + g) + dt;
  }

  outColor = vec4(linearToSrgb(max(result, vec3(0.0))), 1.0);
}`;

const createImageTexture = (
  gl: WebGL2RenderingContext,
  image: HTMLImageElement,
  internalFormat: number,
  filter: number,
) =>
  twgl.createTexture(gl, {
    src: image,
    internalFormat,
    format: gl.RGBA,
    type: gl.UNSIGNED_BYTE,
    wrap: gl.CLAMP_TO_EDGE,
    min: filter,
    mag: filter,
  });

const isRendererReady = (): boolean => {
  if (!state) return false;
  const s = state;
  return LAYERS.every((id) => s.layerTextures.has(id));
};

const clearSceneTextures = (s: State): void => {
  s.layerTextures.forEach((t) => s.gl.deleteTexture(t));
  s.maskTextures.forEach((t) => s.gl.deleteTexture(t));
  s.layerTextures.clear();
  s.maskTextures.clear();
  s.maskData.clear();
};

const ensureContext = (): State => {
  if (state) return state;

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const gl = canvas.getContext('webgl2', {
    premultipliedAlpha: false,
    antialias: false,
    preserveDrawingBuffer: true,
  });
  if (!gl) throw new Error('WebGL2 is not supported');

  gl.disable(gl.BLEND);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.SCISSOR_TEST);

  const blitProgram = twgl.createProgramInfo(gl, [FULLSCREEN_PASS_VS, BLIT_FS]);
  const materialProgram = twgl.createProgramInfo(gl, [
    FULLSCREEN_PASS_VS,
    MATERIAL_FS,
  ]);
  const compositeProgram = twgl.createProgramInfo(gl, [
    FULLSCREEN_PASS_VS,
    COMPOSITE_FS,
  ]);

  const fullScreenBuffer = twgl.createBufferInfoFromArrays(gl, {
    a_position: {
      numComponents: 2,
      data: new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    },
  });

  state = {
    gl,
    canvas,
    layerTextures: new Map(),
    maskTextures: new Map(),
    maskData: new Map(),
    materialCache: new Map(),
    diffuseFramebuffer: gl.createFramebuffer()!,
    diffuseTexture: gl.createTexture()!,
    blitProgram,
    materialProgram,
    compositeProgram,
    fullScreenBuffer,
    readBuffer: new Uint8Array(0),
  };
  return state;
};

const loadMaskData = (image: HTMLImageElement): Uint8ClampedArray => {
  const cv = document.createElement('canvas');
  cv.width = image.naturalWidth;
  cv.height = image.naturalHeight;
  const ctx = cv.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(image, 0, 0);
  return ctx.getImageData(0, 0, cv.width, cv.height).data;
};

const ensureMaterialTexture = (url: string): Promise<MaterialEntry | null> => {
  const s = ensureContext();
  const cached = s.materialCache.get(url);
  if (cached) {
    return cached instanceof Promise ? cached : Promise.resolve(cached);
  }

  const promise = ensureLoadedImage(url, 'anonymous')
    .then((image) => {
      const tex = twgl.createTexture(s.gl, {
        src: image,
        internalFormat: s.gl.RGBA8,
        format: s.gl.RGBA,
        type: s.gl.UNSIGNED_BYTE,
        wrap: s.gl.REPEAT,
        min: s.gl.LINEAR_MIPMAP_LINEAR,
        mag: s.gl.LINEAR,
      });

      const entry: MaterialEntry = {
        texture: tex,
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
      s.materialCache.set(url, entry);
      return entry;
    })
    .catch((err) => {
      console.error('[webglRenderer] material load failed', url, err);
      s.materialCache.delete(url);
      return null;
    });

  s.materialCache.set(url, promise);
  return promise;
};

const resizeIfNeeded = (s: State, width: number, height: number): void => {
  if (s.canvas.width === width && s.canvas.height === height) return;
  s.canvas.width = width;
  s.canvas.height = height;

  const gl = s.gl;
  gl.bindTexture(gl.TEXTURE_2D, s.diffuseTexture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA8,
    width,
    height,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  gl.bindFramebuffer(gl.FRAMEBUFFER, s.diffuseFramebuffer);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    s.diffuseTexture,
    0,
  );

  s.readBuffer = new Uint8Array(width * height * 4);
};

export const initializeRenderer = async (
  images: { key: string; url: string }[],
): Promise<void> => {
  const s = ensureContext();
  const gl = s.gl;
  clearSceneTextures(s);

  const loaded = await Promise.all(
    images.map(async ({ key, url }) => ({
      key,
      image: await ensureLoadedImage(url, 'anonymous'),
    })),
  );

  if (loaded.length === 0) return;

  const reference = loaded[0].image;
  resizeIfNeeded(s, reference.naturalWidth, reference.naturalHeight);

  for (const { key, image } of loaded) {
    if (key.startsWith('layer')) {
      s.layerTextures.set(
        key,
        createImageTexture(gl, image, gl.SRGB8_ALPHA8, gl.LINEAR),
      );
    } else {
      s.maskTextures.set(
        key,
        createImageTexture(gl, image, gl.RGBA8, gl.LINEAR),
      );
      s.maskData.set(key, loadMaskData(image));
    }
  }
};

export const isRendererInitialized = (): boolean => isRendererReady();

export const getCanvasSize = (): { width: number; height: number } | null => {
  if (!isRendererReady()) return null;
  return { width: state!.canvas.width, height: state!.canvas.height };
};

export const preloadMaterialTexture = (url: string): void => {
  if (!isRendererReady()) return;
  void ensureMaterialTexture(url);
};

export const resetRenderer = (): void => {
  if (!state) return;
  const s = state;
  clearSceneTextures(s);
  s.materialCache.forEach((m) => {
    if (!(m instanceof Promise)) s.gl.deleteTexture(m.texture);
  });
  s.materialCache.clear();
};

export const areCoordsInMask = (key: string, x: number, y: number): boolean => {
  if (!isRendererReady()) return false;
  const s = state!;
  const width = s.canvas.width;
  if (x < 0 || y < 0 || x >= width || y >= s.canvas.height) return false;
  const data = s.maskData.get(key);
  if (!data) return false;
  return data[(y * width + x) * 4] > 128;
};

type SegmentTransform = {
  segmentCount: number;
  matrices: Float32Array;
  segmentCorners: Float32Array;
  cornerLines: Float32Array;
};

type ResolvedMapping = {
  mask: WebGLTexture;
  color: [number, number, number];
  material?: MaterialEntry;
  segments?: SegmentTransform;
  sourceQuadSize?: [number, number];
  scaledTextureSize?: [number, number];
  rotation?: number;
};

const getCornerLineEquation = (
  [top, bottom]: [Point, Point],
  rightPoint: Point,
): [number, number, number] => {
  const a = top[1] - bottom[1];
  const b = bottom[0] - top[0];
  const c = -(a * top[0] + b * top[1]);
  const sign = a * rightPoint[0] + b * rightPoint[1] + c >= 0 ? 1 : -1;
  return [a * sign, b * sign, c * sign];
};

// Homography coefficients to a column-major mat3.
// prettier-ignore
const toMat3 = (h: number[]): number[] => [
  h[0], h[3], h[6],
  h[1], h[4], h[7],
  h[2], h[5], 1,
];

const buildSceneTransform = (
  textureWidth: number,
  textureHeight: number,
  realTextureWidth: number,
  realTextureHeight: number,
  realObjectWidth: number,
  realObjectHeight: number,
  corners: Point[],
  cornerLines?: Point[][],
): {
  segments: SegmentTransform;
  sourceQuadSize: [number, number];
  scaledTextureSize: [number, number];
} | null => {
  if (
    realObjectWidth === 0 ||
    realObjectHeight === 0 ||
    realTextureWidth === 0 ||
    realTextureHeight === 0 ||
    textureWidth === 0 ||
    textureHeight === 0
  ) {
    return null;
  }

  const scaledTextureWidth = Math.min(
    16384,
    Math.max(1, Math.round(textureWidth)),
  );
  const scaledTextureHeight = Math.min(
    16384,
    Math.max(1, Math.round(textureHeight)),
  );

  const sourceQuadWidth =
    (realObjectWidth / realTextureWidth) * scaledTextureWidth;
  const sourceQuadHeight =
    (realObjectHeight / realTextureHeight) * scaledTextureHeight;

  const halfW = sourceQuadWidth / 2;
  const halfH = sourceQuadHeight / 2;

  const projectionSegments = buildProjectionSegments(
    corners,
    cornerLines,
    -halfW,
    sourceQuadWidth,
    MAX_GRID_SEGMENTS,
  );

  const segments: SegmentTransform = {
    segmentCount: projectionSegments.length,
    matrices: new Float32Array(MAX_GRID_SEGMENTS * 9),
    segmentCorners: new Float32Array(MAX_GRID_SEGMENTS * 8),
    cornerLines: new Float32Array((MAX_GRID_SEGMENTS - 1) * 3),
  };

  for (let i = 0; i < projectionSegments.length; i++) {
    const { quad, u0, u1 } = projectionSegments[i];
    const [tl, tr, br, bl] = quad;

    const transform = getPerspectiveTransform(
      tl[0],
      tl[1],
      tr[0],
      tr[1],
      br[0],
      br[1],
      bl[0],
      bl[1],
      u0,
      -halfH,
      u1,
      -halfH,
      u1,
      halfH,
      u0,
      halfH,
    );
    if (!transform) return null;

    segments.matrices.set(toMat3(transform), i * 9);
    segments.segmentCorners.set([tl, tr, br, bl].flat(), i * 8);
    if (i > 0) {
      segments.cornerLines.set(
        getCornerLineEquation([tl, bl], corners[1]),
        (i - 1) * 3,
      );
    }
  }

  return {
    segments,
    sourceQuadSize: [sourceQuadWidth, sourceQuadHeight],
    scaledTextureSize: [scaledTextureWidth, scaledTextureHeight],
  };
};

const resolveMappings = async (
  s: State,
  mappings: Mapping[],
): Promise<ResolvedMapping[]> => {
  const results = await Promise.all(
    mappings.map(async (mapping): Promise<ResolvedMapping | null> => {
      const mask = s.maskTextures.get(mapping.mask);
      if (!mask) return null;

      const fallback = (): ResolvedMapping => ({
        mask,
        color: hexToSrgb(mapping.material ?? '#000000'),
      });

      if (!mapping.is_texture_material || !mapping.material) {
        return fallback();
      }

      const material = await ensureMaterialTexture(mapping.material);
      if (!material) return fallback();

      const transform = buildSceneTransform(
        material.width,
        material.height,
        mapping.real_texture_width,
        mapping.real_texture_height,
        mapping.object_width,
        mapping.object_height,
        mapping.corners,
        mapping.cornerLines,
      );
      if (!transform) return fallback();

      return {
        mask,
        color: [0, 0, 0],
        material,
        segments: transform.segments,
        sourceQuadSize: transform.sourceQuadSize,
        scaledTextureSize: transform.scaledTextureSize,
        rotation: mapping.rotation,
      };
    }),
  );

  return results.filter((r): r is ResolvedMapping => r !== null);
};

export const render = async (mappings: Mapping[]): Promise<Uint8Array> => {
  if (!isRendererReady()) {
    throw new Error('[webglRenderer] renderer not initialized');
  }
  const s = state!;
  const gl = s.gl;
  const { width, height } = s.canvas;

  const layers = LAYERS.map((id) => {
    const tex = s.layerTextures.get(id);
    if (!tex) throw new Error(`[webglRenderer] missing required layer ${id}`);
    return tex;
  });
  const [diffuseLayer, lightingLayer, giLayer, detailLayer] = layers;
  const grayLayer = s.layerTextures.get('layer_gray_rendering');

  const resolved = await resolveMappings(s, mappings);

  gl.viewport(0, 0, width, height);

  gl.bindFramebuffer(gl.FRAMEBUFFER, s.diffuseFramebuffer);
  gl.useProgram(s.blitProgram.program);
  twgl.setBuffersAndAttributes(gl, s.blitProgram, s.fullScreenBuffer);
  twgl.setUniforms(s.blitProgram, { u_texture: diffuseLayer });
  twgl.drawBufferInfo(gl, s.fullScreenBuffer, gl.TRIANGLE_STRIP);

  gl.useProgram(s.materialProgram.program);
  twgl.setBuffersAndAttributes(gl, s.materialProgram, s.fullScreenBuffer);
  twgl.setUniforms(s.materialProgram, { u_imageSize: [width, height] });

  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(
    gl.SRC_ALPHA,
    gl.ONE_MINUS_SRC_ALPHA,
    gl.ONE,
    gl.ONE_MINUS_SRC_ALPHA,
  );

  for (const mapping of resolved) {
    const isTexture = !!mapping.material;
    twgl.setUniforms(s.materialProgram, {
      u_mask: mapping.mask,
      u_isTexture: isTexture ? 1 : 0,
      u_color: mapping.color,
      u_material: mapping.material?.texture,
      ...(mapping.segments && {
        u_segmentCount: mapping.segments.segmentCount,
        u_matrices: mapping.segments.matrices,
        u_segmentCorners: mapping.segments.segmentCorners,
        u_cornerLines: mapping.segments.cornerLines,
      }),
      u_sourceQuadSize: mapping.sourceQuadSize ?? [1, 1],
      u_scaledTextureSize: mapping.scaledTextureSize ?? [1, 1],
      u_rotation: mapping.rotation ?? 0,
    });
    twgl.drawBufferInfo(gl, s.fullScreenBuffer, gl.TRIANGLE_STRIP);
  }

  gl.disable(gl.BLEND);

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.useProgram(s.compositeProgram.program);
  twgl.setBuffersAndAttributes(gl, s.compositeProgram, s.fullScreenBuffer);
  twgl.setUniforms(s.compositeProgram, {
    u_diffuse: s.diffuseTexture,
    u_lighting: lightingLayer,
    u_gi: giLayer,
    u_detail: detailLayer,
    u_has_gray: grayLayer ? 1 : 0,
    u_gray: grayLayer ?? s.diffuseTexture,
  });
  twgl.drawBufferInfo(gl, s.fullScreenBuffer, gl.TRIANGLE_STRIP);

  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, s.readBuffer);
  return s.readBuffer;
};

type ViewAssetState = Pick<
  InspirationEditorState,
  'inspiration' | 'currentViewId' | 'componentOverwrites' | 'editMasterData'
>;

export const loadViewAssets = async (
  inspiration: DetailedInspiration,
  state: ViewAssetState,
  viewId: number,
): Promise<boolean> => {
  const currentView = getCurrentView(state, viewId);
  if (!currentView) return false;

  const componentIdsInView = new Set(getVisibleComponentIds(state, viewId));

  const masks = Object.entries(inspiration.render.masks)
    .filter(([id]) => componentIdsInView.has(id))
    .map(([id, value]) => ({
      key: `mask_${id}`,
      url: value.replace('{view}', String(currentView.id)),
    }));

  const viewLayerKeys = new Set(currentView.layers ?? []);

  const layers = Object.entries(inspiration.render.layers)
    .filter(
      ([key]) => LAYERS.includes(`layer_${key}`) || viewLayerKeys.has(key),
    )
    .map(([key, value]) => ({
      key: `layer_${key}`,
      url: value.replace('{view}', String(currentView.id)),
    }));

  if (isRendererInitialized()) resetRenderer();

  await initializeRenderer([...masks, ...layers]);

  return true;
};
